// @ts-nocheck -- Svelte rune store; session invariants are enforced at runtime.
// Auth store — Svelte 5 port of the Next `useAuth` hook. App-global singleton.
//
// Firebase is loaded LAZILY (dynamic import inside the methods) so it never
// enters the prerender/initial bundle — the store module is safe to import from
// the always-rendered Navbar. When the VITE_FIREBASE_* env vars are absent the
// app runs in anonymous-only mode (`configured = false`) and the auth UI hides.
//
// Call auth.init() once on the client (root layout onMount).

import { tick } from 'svelte';
import { goto } from '$app/navigation';
import { page } from '$app/state';
import {
	EMAIL_CALLBACK_PATH, EMAIL_PENDING_KEY, EMAIL_REAUTH_INTENTS,
	normalizeEmail, parseEmailCallback, readPendingEmailLink,
	pendingMatchesCallback, safeAuthReturnPath
} from '../auth/emailLink.js';

const IS_CONFIGURED = !!(
	import.meta.env.VITE_FIREBASE_API_KEY &&
	import.meta.env.VITE_FIREBASE_AUTH_DOMAIN &&
	import.meta.env.VITE_FIREBASE_PROJECT_ID
);

const REAUTH_INTENT_KEY = 'mylocation.reauth-intent';
const DELETE_REAUTH_INTENT = 'delete_cloud_data';
// A re-sign-in can be asked for by more than one screen. The intent travels
// through the Google round trip so the right screen picks the action back up,
// and nothing but account deletion ever reopens the deletion dialog.
const REAUTH_INTENTS = EMAIL_REAUTH_INTENTS;

/**
 * True inside the embedded browser of a social or chat app (Instagram,
 * Facebook, Messenger, LinkedIn, Snapchat, TikTok, Line, a generic Android
 * WebView). Google refuses to sign people in there, so screens offer "open in
 * your browser" instead of a sign-in button that ends on a Google error page.
 */
export function isInAppBrowser() {
	if (typeof navigator === 'undefined') return false;
	return /FBAN|FBAV|FB_IAB|FBIOS|Instagram|LinkedInApp|Snapchat|musical_ly|BytedanceWebview|TikTok|Line\/|MicroMessenger|; wv\)/i
		.test(navigator.userAgent || '');
}

/** Sign-in errors that mean "the person closed the window": say nothing. */
const SILENT_SIGN_IN_ERRORS = new Set([
	'auth/popup-closed-by-user',
	'auth/cancelled-popup-request',
	'auth/user-cancelled',
	'auth/redirect-cancelled-by-user'
]);

/** @returns {'' | 'offline' | 'mismatch' | 'storage' | 'failed'} */
function signInErrorKind(error) {
	const code = error?.code || '';
	if (SILENT_SIGN_IN_ERRORS.has(code)) return '';
	if (code === 'storage_volatile') return 'storage';
	if (code === 'auth/network-request-failed') return 'offline';
	if (['auth/user-mismatch', 'account_changed'].includes(code)) return 'mismatch';
	return 'failed';
}

function authError(code) {
	return Object.assign(new Error(code), { code });
}

function emailErrorKind(error) {
	switch (error?.code) {
		case 'auth/invalid-email': return 'invalid_email';
		case 'auth/expired-action-code': return 'expired_link';
		case 'auth/invalid-action-code':
		case 'auth/invalid-credential': return 'invalid_link';
		case 'auth/too-many-requests': return 'too_many_requests';
		case 'auth/not-signed-in': return 'not_signed_in';
		case 'auth/cancelled': return '';
		default: return signInErrorKind(error);
	}
}

function prefersRedirect() {
	if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
	return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '') ||
		window.matchMedia?.('(pointer: coarse)').matches === true;
}

/**
 * Signing in by leaving the page for Google reloads it. In a window that cannot
 * store (a private window, a second tab), places and routes added there would
 * be gone on return. True only when there is something to lose.
 */
async function reloadWouldLoseData() {
	try {
		const { isStorageVolatile } = await import('#lib/sqlite/client.js');
		if (!isStorageVolatile()) return false;
		const { countGuestEntities } = await import('#lib/syncDB.js');
		return (await countGuestEntities('')) > 0;
	} catch {
		return false;
	}
}

async function redirectToGoogle(authInstance, provider, sdk) {
	// No pre-check here: sign-in on phones goes straight to Google, as it always has.
	return sdk.signInWithRedirect(authInstance, provider, sdk.browserPopupRedirectResolver);
}

function popupCannotOpen(error) {
	return ['auth/popup-blocked', 'auth/operation-not-supported-in-this-environment']
		.includes(error?.code);
}

class Auth {
	#readyResolve = null;
	/** @type {import('@firebase/auth').User | null} */
	user = $state(null);
	#loading = $state(IS_CONFIGURED); // true until the first auth-state callback
	get loading() {
		return this.#loading;
	}
	set loading(value) {
		this.#loading = !!value;
		if (!value) {
			this.#readyResolve?.();
			this.#readyResolve = null;
		}
	}
	sessionEpoch = $state(0);
	suspended = $state(false);
	reauthContinuation = $state(null);
	/** True from the tap on a sign-in button until Google answers or the page leaves. */
	signingIn = $state(false);
	/** Why the last sign-in did not work: '' | 'offline' | 'mismatch' | 'storage' | 'failed'. Screens show it inline. */
	lastError = $state('');
	configured = IS_CONFIGURED;
	/** @type {{id: number, stage: 'providers'|'email'|'sent'|'complete'|'success', email: string, reauthenticating: boolean, busy: boolean, error: string, returnTo: string, expectedUid?: string, intent?: string | null} | null} */
	authDialog = $state(null);
	#dialogSequence = 0;
	#authRevision = 0;
	#authOperation = null;
	#firebaseAuth = null;
	#deferredAuthUser = null;
	#signingOut = $state(false);
	get signingOut() { return this.#signingOut; }
	#signOutTask = null;
	#emailCallback = null;
	#callbackAutoAttempted = null;
	#callbackVisited = false;
	#lastCapturedURL = null;
	#callbackScrubTask = null;

	/** Resolves once the first sign-in state is known (or sign-in could not start). */
	ready = new Promise((resolve) => {
		if (!IS_CONFIGURED || typeof window === 'undefined') resolve();
		else this.#readyResolve = resolve;
	});

	#settle() {
		this.loading = false;
	}

	#unsub = null;
	#started = false;
	#teardownHandlers = new Set();
	#accountOperations = new Map();

	#setUser(nextUser) {
		const previousUid = this.user?.uid || '';
		const nextUid = nextUser?.uid || '';
		if (previousUid === nextUid) {
			this.user = nextUser;
			return;
		}

		// Clear the old identity synchronously. Registered data stores invalidate
		// their own generations before any asynchronous cleanup is awaited.
		this.user = null;
		this.suspended = false;
		this.#authRevision += 1;
		this.reauthContinuation = null;
		this.sessionEpoch += 1;
		const teardown = previousUid ? this.#runTeardown(previousUid) : Promise.resolve();
		this.user = nextUser;
		this.sessionEpoch += 1;
		return teardown;
	}

	async #runTeardown(uid) {
		const pending = [];
		for (const handler of this.#teardownHandlers) {
			try {
				pending.push(Promise.resolve(handler(uid)));
			} catch (error) {
				pending.push(Promise.reject(error));
			}
		}
		pending.push(...(this.#accountOperations.get(uid) || []));
		const results = await Promise.allSettled(pending);
		for (const result of results) {
			if (result.status === 'rejected') console.error('session teardown failed', result.reason);
		}
	}

	async init() {
		if (typeof window === 'undefined') return;
		this.#captureEmailCallback();
		if (!this.configured || this.#started) return;
		this.#started = true;
		let authInstance = null;
		let firebaseAuth = null;
		try {
			await this.#callbackScrubTask;
			firebaseAuth = await import('#lib/firebase.js');
			authInstance = firebaseAuth.getAuthInstance();
		} catch (error) {
			// The sign-in code could not be loaded (offline, blocked). Never leave
			// every screen waiting on a loading state that cannot end.
			this.#started = false;
			this.#settle();
			throw error;
		}
		if (!authInstance || !firebaseAuth) {
			this.#settle();
			return;
		}
		this.#firebaseAuth = authInstance;
		const { onAuthStateChanged } = firebaseAuth;
		this.#unsub = onAuthStateChanged(authInstance, (u) => {
			if (authInstance.currentUser !== u) return;
			// Do not expose an SDK result before cancellation/session checks.
			if (u && this.#authOperation?.signingIn) {
				this.#deferredAuthUser = u;
				return;
			}
			this.#setUser(u);
			this.#settle();
		}, (error) => {
			console.error('auth state failed', error);
			this.#settle();
		});
		let intent = null;
		try {
			try { intent = window.sessionStorage.getItem(REAUTH_INTENT_KEY); } catch { /* continuation is optional */ }
			const { completeRedirect } = await import('../auth/redirectResult.js');
			const result = await completeRedirect(authInstance);
			if (result?.user && REAUTH_INTENTS.has(intent)) {
				// Never resume a destructive action automatically after navigation.
				// The owning screen re-offers it so it needs a fresh user gesture.
				this.reauthContinuation = intent;
			}
		} catch (error) {
			console.error('authentication redirect failed', error?.code || 'unknown');
			this.lastError = signInErrorKind(error);
		} finally {
			try { window.sessionStorage.removeItem(REAUTH_INTENT_KEY); } catch (_) {}
		}
		await this.ready;
		await this.#completeCapturedCallback();
	}

	async signIn() {
		const revision = this.#authRevision;
		await this.ready;
		if (revision !== this.#authRevision) throw authError('auth/cancelled');
		const operation = this.#beginAuthOperation(true);
		try {
			const { authInstance, sdk } = await this.#firebase(() => import('../auth/providerActions.js'));
			this.#checkAuthOperation(operation);
			if (authInstance.currentUser) throw authError('account_changed');
			const provider = new sdk.GoogleAuthProvider();
			if (prefersRedirect()) return redirectToGoogle(authInstance, provider, sdk);
			let result;
			try {
				result = await sdk.signInWithPopup(authInstance, provider, sdk.browserPopupRedirectResolver);
			} catch (error) {
				this.#checkAuthOperation(operation);
				if (!popupCannotOpen(error)) throw error;
				return redirectToGoogle(authInstance, provider, sdk);
			}
			await this.#acceptSignIn(result.user, authInstance, sdk, operation);
			return result.user;
		} finally { this.#endAuthOperation(operation); }
	}

	/** Existing Google entry point, used inside the provider chooser. */
	async startSignIn() {
		if (this.signingIn || this.#authOperation || this.#signingOut) return false;
		this.signingIn = true;
		this.lastError = '';
		const dialog = this.authDialog;
		if (dialog) { dialog.busy = true; dialog.error = ''; }
		try {
			const user = await this.signIn();
			if (user && this.authDialog === dialog) this.authDialog = null;
			return !!user;
		} catch (error) {
			const kind = error?.code === 'auth/cancelled' ? '' : signInErrorKind(error);
			if (dialog && this.authDialog === dialog) dialog.error = kind;
			else if (!dialog) this.lastError = kind;
			return false;
		} finally {
			this.signingIn = false;
			if (this.authDialog === dialog && dialog) dialog.busy = false;
		}
	}

	async #firebase(loadActions) {
		const { getAuthInstance } = await import('#lib/firebase.js');
		const authInstance = getAuthInstance();
		if (!authInstance) throw authError('firebase_not_configured');
		this.#firebaseAuth = authInstance;
		await authInstance.authStateReady();
		return { authInstance, sdk: loadActions ? await loadActions() : null };
	}

	#beginAuthOperation(signingIn = false) {
		if (this.#authOperation || this.suspended || this.#signingOut) throw authError('auth/operation-in-progress');
		const operation = { revision: this.#authRevision, signingIn };
		this.#authOperation = operation;
		return operation;
	}

	#checkAuthOperation(operation) {
		if (this.#authOperation !== operation || operation.revision !== this.#authRevision) {
			throw authError('auth/cancelled');
		}
	}

	#endAuthOperation(operation) {
		if (this.#authOperation !== operation) return;
		this.#authOperation = null;
		const deferred = this.#deferredAuthUser;
		this.#deferredAuthUser = null;
		if (deferred && this.#firebaseAuth?.currentUser === deferred) this.#setUser(deferred);
	}

	async #acceptSignIn(user, authInstance, sdk, operation) {
		try {
			this.#checkAuthOperation(operation);
			if (authInstance.currentUser !== user) throw authError('account_changed');
		} catch (error) {
			// A cancelled Firebase request can still finish. The operation remains
			// owned until its result is removed, so a newer sign-in cannot overlap.
			if (authInstance.currentUser === user) await sdk.signOut(authInstance);
			throw error;
		}
		this.#setUser(user);
	}

	#currentReturnPath() {
		return safeAuthReturnPath(typeof window === 'undefined' ? '' :
			window.location.pathname + window.location.search + window.location.hash);
	}

	#readPendingEmail() {
		try {
			const pending = readPendingEmailLink(window.localStorage.getItem(EMAIL_PENDING_KEY));
			if (!pending) window.localStorage.removeItem(EMAIL_PENDING_KEY);
			return pending;
		} catch { return null; }
	}

	#clearPendingEmail() {
		try { window.localStorage.removeItem(EMAIL_PENDING_KEY); } catch { /* best effort */ }
	}

	#newDialog(stage, extra = {}) {
		this.authDialog = {
			stage, email: '', reauthenticating: false,
			busy: false, error: '', returnTo: this.#currentReturnPath(), ...extra, id: ++this.#dialogSequence
		};
	}

	openSignIn() {
		if (this.#authOperation || this.#signingOut || this.user || !this.configured) return;
		this.#emailCallback = null;
		this.#newDialog('providers');
		this.lastError = '';
	}

	openEmailSignIn() {
		if (this.#authOperation || this.#signingOut) return;
		const previous = this.authDialog;
		this.#emailCallback = null;
		if (previous?.reauthenticating && this.user) {
			this.#newDialog('email', { ...previous, stage: 'email', busy: false, error: '', email: this.user.email || '' });
		} else {
			this.#newDialog('email', { email: previous?.email || this.#readPendingEmail()?.email || '', returnTo: previous?.returnTo || this.#currentReturnPath() });
		}
	}

	closeSignIn() {
		this.#authRevision += 1;
		this.authDialog = null;
		this.#emailCallback = null;
	}

	#captureEmailCallback() {
		const url = new URL(window.location.href);
		const isCallback = ['/auth/email', EMAIL_CALLBACK_PATH].includes(url.pathname);
		const rootAction = url.pathname === '/' && (url.searchParams.has('oobCode') || url.searchParams.get('mode') === 'signIn');
		if (!isCallback && !rootAction) return false;
		if ((this.#callbackVisited && !url.search) || this.#lastCapturedURL === url.href) return false;
		this.#callbackVisited = true;
		this.#lastCapturedURL = url.href;
		const parsed = parseEmailCallback(url.href, {
			apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
			projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
			authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
			origin: window.location.origin
		});
		// Capture once before yielding, then let SvelteKit finish mounting before
		// replacing the route. Shallow replaceState retains the old page URL in
		// history.state; goto also removes credentials from that router state.
		// Callback responses carry no-referrer/no-store during this short wait.
		this.#callbackScrubTask = Promise.resolve().then(async () => {
			await tick();
			if (window.location.href !== url.href) return;
			await goto(url.pathname, { replace: true, reset: false, state: page.state });
		});
		const pending = this.#readPendingEmail();
		const matches = parsed && pendingMatchesCallback(pending, parsed);
		this.#emailCallback = parsed ? { ...parsed, pending: matches ? pending : null } : null;
		this.#newDialog('complete', {
			email: matches ? pending.email : '',
			reauthenticating: parsed?.purpose === 'reauthenticate',
			returnTo: matches ? pending.returnTo : '/app/',
			error: parsed ? '' : 'invalid_link'
		});
		return true;
	}

	/** Also called by the callback route for installed-PWA/SPA navigation. */
	async handleEmailCallback() {
		if (typeof window === 'undefined') return;
		this.#captureEmailCallback();
		await this.init();
		await this.#callbackScrubTask;
		await this.ready;
		await this.#completeCapturedCallback();
	}

	async #completeCapturedCallback() {
		const callback = this.#emailCallback;
		if (!callback || this.#callbackAutoAttempted === callback) return;
		this.#callbackAutoAttempted = callback;
		if (this.authDialog?.reauthenticating && this.user) {
			this.authDialog.expectedUid = this.user.uid;
			this.authDialog.intent = callback.pending?.uid === this.user.uid ? callback.pending.intent : null;
		}
		if (callback.purpose === 'reauthenticate' && !this.user) {
			if (this.authDialog) this.authDialog.error = 'not_signed_in';
			return;
		}
		if (callback.pending?.email) await this.completeEmailLink(callback.pending.email);
	}

	async sendEmailLink(input) {
		const dialog = this.authDialog;
		await this.ready;
		if (this.authDialog !== dialog) return false;
		if (!dialog || dialog.busy || this.#authOperation || this.suspended || this.#signingOut) return false;
		const email = normalizeEmail(input);
		if (!email) { dialog.error = 'invalid_email'; return false; }
		const operation = this.#beginAuthOperation();
		dialog.busy = true;
		dialog.error = '';
		try {
			const { authInstance, sdk } = await this.#firebase(() => import('../auth/emailActions.js'));
			this.#checkAuthOperation(operation);
			const current = authInstance.currentUser;
			if (dialog.reauthenticating) {
				if (!current) throw authError('auth/not-signed-in');
				if (current.uid !== dialog.expectedUid || current.email?.toLowerCase() !== email.toLowerCase()) throw authError('account_changed');
			} else if (current) throw authError('account_changed');
			const requestId = crypto.randomUUID();
			const url = new URL(EMAIL_CALLBACK_PATH, window.location.origin);
			url.searchParams.set('state', requestId);
			if (dialog.reauthenticating) url.searchParams.set('purpose', 'reauthenticate');
			await sdk.sendSignInLinkToEmail(authInstance, email, { url: url.href, handleCodeInApp: true });
			this.#checkAuthOperation(operation);
			if (authInstance.currentUser !== current) throw authError('account_changed');
			const pending = {
				email, purpose: dialog.reauthenticating ? 'reauthenticate' : 'signIn',
				uid: current?.uid || '', intent: dialog.intent || null, requestId,
				returnTo: safeAuthReturnPath(dialog.returnTo), sentAt: Date.now()
			};
			try { window.localStorage.setItem(EMAIL_PENDING_KEY, JSON.stringify(pending)); } catch { /* completion will ask again */ }
			dialog.email = email;
			dialog.stage = 'sent';
			return true;
		} catch (error) {
			if (this.authDialog === dialog) dialog.error = emailErrorKind(error);
			return false;
		} finally {
			this.#endAuthOperation(operation);
			if (this.authDialog === dialog) dialog.busy = false;
		}
	}

	async completeEmailLink(input) {
		const dialog = this.authDialog;
		const callback = this.#emailCallback;
		await this.#callbackScrubTask;
		await this.ready;
		if (this.authDialog !== dialog || this.#emailCallback !== callback) return false;
		if (!dialog || dialog.busy || this.#authOperation || this.suspended || this.#signingOut) return false;
		const email = normalizeEmail(input);
		if (!email) { dialog.error = 'invalid_email'; return false; }
		if (!callback) { dialog.error = 'invalid_link'; return false; }
		const operation = this.#beginAuthOperation();
		dialog.busy = true;
		dialog.error = '';
		try {
			const { authInstance, sdk } = await this.#firebase(() => import('../auth/emailActions.js'));
			this.#checkAuthOperation(operation);
			if (!sdk.isSignInWithEmailLink(authInstance, callback.link)) throw authError('auth/invalid-action-code');
			const current = authInstance.currentUser;
			if (current) {
				if (current.email?.toLowerCase() !== email.toLowerCase() ||
					(callback.pending?.uid && callback.pending.uid !== current.uid)) throw authError('account_changed');
				const credential = sdk.EmailAuthProvider.credentialWithLink(email, callback.link);
				const result = await sdk.reauthenticateWithCredential(current, credential);
				this.#checkAuthOperation(operation);
				if (authInstance.currentUser !== current || result.user.uid !== current.uid) throw authError('account_changed');
				// Firebase reauthentication validates the UID and installs the fresh
				// ID token before resolving. An extra refresh could fail after the
				// one-use link was already consumed.
				dialog.reauthenticating = true;
				if (callback.pending?.uid === current.uid && REAUTH_INTENTS.has(callback.pending.intent)) {
					this.reauthContinuation = callback.pending.intent;
				}
			} else {
				if (callback.purpose === 'reauthenticate') throw authError('auth/not-signed-in');
				operation.signingIn = true;
				const result = await sdk.signInWithEmailLink(authInstance, email, callback.link);
				await this.#acceptSignIn(result.user, authInstance, sdk, operation);
			}
			this.#clearPendingEmail();
			this.#emailCallback = null;
			dialog.email = email;
			dialog.stage = 'success';
			return true;
		} catch (error) {
			if (this.authDialog === dialog) dialog.error = emailErrorKind(error);
			return false;
		} finally {
			this.#endAuthOperation(operation);
			if (this.authDialog === dialog) dialog.busy = false;
		}
	}

	clearError() {
		this.lastError = '';
	}

	signOut() {
		if (this.#signOutTask) return this.#signOutTask;
		this.#signingOut = true;
		this.#signOutTask = this.#performSignOut().finally(() => {
			this.#signingOut = false;
			this.#signOutTask = null;
		});
		return this.#signOutTask;
	}

	async #performSignOut() {
		this.closeSignIn();
		this.#clearPendingEmail();
		this.reauthContinuation = null;
		const previousUser = this.user;
		// Account-owned map/friend state disappears before Firebase or the network
		// can delay sign-out. If Firebase rejects, restore its actual current user.
		const teardown = this.#setUser(null);
		const { getAuthInstance, signOut } = await import('#lib/firebase.js');
		const authInstance = getAuthInstance();
		if (!authInstance) return;
		try {
			await teardown;
			await signOut(authInstance);
		} catch (error) {
			this.#setUser(authInstance.currentUser || previousUser);
			throw error;
		}
	}

	/** Returns the Firebase ID token (for Worker calls) or null when signed out. */
	async getIdToken(forceRefresh = false) {
		const { getAuthInstance } = await import('#lib/firebase.js');
		const current = getAuthInstance()?.currentUser;
		if (!current) return null;
		return current.getIdToken(forceRefresh);
	}

	/** Immutable identity snapshot for requests and async store operations. */
	captureSession() {
		if (!this.user || this.suspended) return null;
		return { uid: this.user.uid, epoch: this.sessionEpoch, user: this.user };
	}

	isSessionCurrent(session) {
		return !!session && !this.suspended && this.sessionEpoch === session.epoch &&
			this.user === session.user && this.user?.uid === session.uid;
	}

	/** Register a store cleanup that runs before account deletion/sign-out completes. */
	registerSessionTeardown(handler) {
		if (typeof handler !== 'function') return () => {};
		this.#teardownHandlers.add(handler);
		return () => this.#teardownHandlers.delete(handler);
	}

	trackAccountOperation(session, operation) {
		if (!session?.uid || !operation || typeof operation.then !== 'function') return operation;
		let operations = this.#accountOperations.get(session.uid);
		if (!operations) {
			operations = new Set();
			this.#accountOperations.set(session.uid, operations);
		}
		operations.add(operation);
		const remove = () => {
			operations.delete(operation);
			if (operations.size === 0) this.#accountOperations.delete(session.uid);
		};
		operation.then(remove, remove);
		return operation;
	}

	/** Stop account-scoped work while retaining Firebase authority for deletion. */
	async quiesceCurrentSession(expectedUid) {
		const uid = this.user?.uid;
		if (!uid || (expectedUid && expectedUid !== uid)) throw new Error('account_changed');
		this.suspended = true;
		this.#authRevision += 1;
		this.sessionEpoch += 1;
		await this.#runTeardown(uid);
	}

	resumeCurrentSession(expectedUid) {
		if (!this.user || this.user.uid !== expectedUid || !this.suspended) return false;
		this.suspended = false;
		this.sessionEpoch += 1;
		return true;
	}

	/** Recent-login proof; email returns to a fresh confirmation, never an action.
	 * @param {string} expectedUid @param {string} [intent]
	 */
	async reauthenticate(expectedUid, intent = DELETE_REAUTH_INTENT) {
		if (!REAUTH_INTENTS.has(intent)) throw authError('unknown_reauth_intent');
		const operation = this.#beginAuthOperation();
		try {
			const { authInstance } = await this.#firebase();
			this.#checkAuthOperation(operation);
			const current = authInstance.currentUser;
			if (!current || current.uid !== expectedUid) throw authError('account_changed');
			const tokenResult = await current.getIdTokenResult(true);
			this.#checkAuthOperation(operation);
			if (authInstance.currentUser !== current) throw authError('account_changed');
			const age = Date.now() - Number(tokenResult.claims?.auth_time) * 1000;
			if (Number.isFinite(age) && age >= 0 && age < 4 * 60 * 1000) return current;

			if (!current.providerData.some((provider) => provider.providerId === 'google.com')) {
				this.#newDialog('email', {
					reauthenticating: true, email: current.email || '', expectedUid,
					intent, returnTo: this.#currentReturnPath()
				});
				throw authError('email_link_required');
			}
			const sdk = await import('../auth/providerActions.js');
			this.#checkAuthOperation(operation);
			if (authInstance.currentUser !== current) throw authError('account_changed');
			const provider = new sdk.GoogleAuthProvider();
			const redirect = async () => {
				try { window.sessionStorage.setItem(REAUTH_INTENT_KEY, intent); } catch { /* best effort */ }
				return sdk.reauthenticateWithRedirect(current, provider, sdk.browserPopupRedirectResolver);
			};
			if (prefersRedirect()) return redirect();
			let result;
			try {
				result = await sdk.reauthenticateWithPopup(current, provider, sdk.browserPopupRedirectResolver);
			} catch (error) {
				this.#checkAuthOperation(operation);
				if (!popupCannotOpen(error)) throw error;
				return redirect();
			}
			this.#checkAuthOperation(operation);
			if (result.user.uid !== expectedUid || authInstance.currentUser !== current) throw authError('account_changed');
			await current.getIdToken(true);
			this.#checkAuthOperation(operation);
			if (authInstance.currentUser !== current) throw authError('account_changed');
			return current;
		} finally { this.#endAuthOperation(operation); }
	}

	consumeReauthContinuation(intent) {
		if (this.reauthContinuation !== intent) return false;
		this.reauthContinuation = null;
		return true;
	}

	async deleteFirebaseAccount(expectedUser) {
		this.closeSignIn();
		this.#clearPendingEmail();
		const { getAuthInstance } = await import('#lib/firebase.js');
		if (!expectedUser || getAuthInstance()?.currentUser !== expectedUser || this.user !== expectedUser) {
			throw new Error('account_changed');
		}
		const { deleteUser } = await import('../auth/accountActions.js');
		if (getAuthInstance()?.currentUser !== expectedUser || this.user !== expectedUser) {
			throw new Error('account_changed');
		}
		await deleteUser(expectedUser);
		if (this.user === expectedUser) this.#setUser(null);
	}

	destroy() {
		this.closeSignIn();
		const uid = this.user?.uid || '';
		if (this.#unsub) {
			this.#unsub();
			this.#unsub = null;
		}
		this.user = null;
		this.suspended = false;
		this.reauthContinuation = null;
		this.signingIn = false;
		this.sessionEpoch += 1;
		if (uid) void this.#runTeardown(uid);
		this.#started = false;
	}
}

export const auth = new Auth();
